import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router';
import { guards } from './route-guards';
import { moduleGate } from './module-gate';
import { sectionComponent } from './section-view';
import { useTenantStore } from '@features/tenant/store/tenant';
import { useHead } from '@unhead/vue';
import { resolveDetectedGeo, isAppAvailable } from '@shared/utils/country-gate';

export const routes: RouteRecordRaw[] = [
  {
    // Country gate landing — shown when the viewer's country is disabled in the
    // Country Registry. Public (no auth) and chromeless.
    path: '/unavailable',
    name: 'CountryUnavailable',
    component: () => import('@/shared/ui/views/CountryUnavailableView.vue'),
    meta: { hideNavbar: true },
  },
  {
    path: '/auth/callback',
    name: 'SsoCallback',
    component: () => import('@/features/auth/views/SsoCallbackView.vue'),
    meta: { hideNavbar: true },
  },
  {
    path: '/',
    name: 'HomeView',
    component: sectionComponent('home'),
    props: (route) => ({
      slug: (route.params.slug = 'home'),
    }),
    // Se elimina guards.ssoMicrosoft, dado que genera conflicto con la auth con google.
    beforeEnter: [guards.checkIfLoggedIn],
  },
  {
    path: '/eventos',
    name: 'ViewEvents',
    meta: { module: 'events' },
    component: sectionComponent('events'),
    beforeEnter: [guards.checkIfLoggedIn, guards.isMobile],
  },
  {
    path: '/events',
    redirect: { name: 'ViewEvents' },
  },
  {
    path: '/collection/',
    beforeEnter: [guards.checkIfLoggedIn, guards.isMobile],
    children: [
      {
        path: ':slug',
        name: 'CollectionView',
        component: () =>
          import('@/features/collection/views/CollectionView.vue'),
        props: (route) => ({ slug: route.params.slug }),
      },
      {
        path: ':collectionSlug/category/:categorySlug',
        name: 'CategoryView',
        component: () => import('@/features/collection/views/CategoryView.vue'),
        props: (route) => ({
          collectionSlug: route.params.collectionSlug,
          categorySlug: route.params.categorySlug,
        }),
      },
    ],
  },
  {
    path: '/visual-category/:slug',
    name: 'VisualCategoryView',
    component: () =>
      import('@/features/collection/views/VisualCategoryView.vue'),
    props: (route) => ({ slug: route.params.slug }),
    beforeEnter: [guards.checkIfLoggedIn, guards.isMobile],
  },
  // Línea 53 - Missions
  {
    path: '/missions',
    name: 'MissionsView',
    meta: { module: 'missions' },
    component: sectionComponent('missions'),
    beforeEnter: [guards.checkIfLoggedIn, guards.isMobile], // Cambiar de mustBeLoggedIn
  },

  // Línea 59 - Rankings (ya está corregido según conversación anterior)
  {
    path: '/rewards',
    name: 'RewardsView',
    meta: { module: 'prizes' },
    component: sectionComponent('rewards'),
    beforeEnter: [guards.checkIfLoggedIn],
  },
  {
    path: '/rankings',
    name: 'RankingsView',
    meta: { module: 'rankings' },
    component: sectionComponent('rankings'),
    beforeEnter: [guards.checkIfLoggedIn, guards.isMobile], // Cambiar de mustBeLoggedIn
  },
  {
    name: 'ExperienceView',
    path: '/experience/:slug',
    component: () => import('@/features/experience/views/ExperienceView.vue'),
    props: (route) => ({
      slug: route.params.slug,
      rankingId: route.query.rankingId,
      rewardType: route.query.rewardType,
    }),
    beforeEnter: [
      guards.checkIfLoggedIn, // Cambiar de mustBeLoggedIn a checkIfLoggedIn
      guards.canEnterRoomUPM,
      guards.isMobile,
    ],
  },
  {
    name: 'EventExperienceView',
    path: '/event-experience/:slug',
    component: () => import('@/features/events/views/EventExperienceView.vue'),
    props: (route) => ({
      slug: route.params.slug,
      eventId: route.query.eventId,
      rewardType: route.query.rewardType,
    }),
    beforeEnter: [
      guards.checkIfLoggedIn,
      guards.canEnterRoomUPM,
      guards.isMobile,
    ],
  },
  {
    name: 'EventView',
    path: '/event/:slug',
    component: () => import('@/features/events/views/EventView.vue'),
    props: (route) => ({ slug: route.params.slug }),
    beforeEnter: [guards.mustBeLoggedIn, guards.isMobile],
  },
  {
    name: 'ProfileView',
    path: '/profile',
    redirect: '/dashboard',
  },
  {
    name: 'SubscriptionDashboardView',
    path: '/dashboard',
    component: sectionComponent('panel'),
    beforeEnter: [guards.mustBeLoggedIn],
  },

  {
    name: 'SubscriptionsView',
    path: '/subscriptions',
    component: sectionComponent('plans'),
    beforeEnter: [guards.checkIfLoggedIn, guards.isMobile],
  },
  {
    // Public, country-aware legal pages (replica of policies.legendaryum.com).
    // Chromeless and always reachable — see the country-gate allowlist below.
    path: '/policies',
    name: 'PoliciesHome',
    component: () => import('@/shared/ui/views/policies/PoliciesHomeView.vue'),
    meta: { hideNavbar: true },
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: () => import('@/shared/ui/views/policies/LegalDocumentView.vue'),
    props: { kind: 'terms' },
    meta: { hideNavbar: true },
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/shared/ui/views/policies/LegalDocumentView.vue'),
    props: { kind: 'privacy' },
    meta: { hideNavbar: true },
  },
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/shared/ui/views/404View.vue'),
    beforeEnter: [guards.checkIfLoggedIn],
  },
];

// Legal pages must always be reachable, even from a country disabled in the registry —
// the country gate never redirects these away.
const COUNTRY_GATE_ALLOWLIST = ['/policies', '/terms', '/privacy'];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (to.path != from.path) {
      return { left: 0, top: 0 };
    }

    return { left: savedPosition?.left, top: savedPosition?.top };
  },
  routes,
});

// Country gate — runs before any route (and before SSO login). If the viewer's
// country is disabled in the Country Registry, send them to the "not available"
// screen. The geo check is cached per page load, so this is a no-op after the
// first navigation. Fail-open when the country is unknown (see isAppAvailable).
router.beforeEach(async (to, _from, next) => {
  // Legal pages bypass the gate entirely (always readable).
  if (COUNTRY_GATE_ALLOWLIST.includes(to.path)) {
    next();
    return;
  }

  const geo = await resolveDetectedGeo();
  const allowed = isAppAvailable(geo);

  if (to.path === '/unavailable') {
    // Re-evaluate the gate every time the screen is shown (e.g. a browser refresh
    // after the user turned a VPN off and is back in an enabled country): if the
    // country is now allowed, send them into the app instead of leaving them stuck.
    allowed ? next({ path: '/', replace: true }) : next();
    return;
  }

  if (!allowed) {
    next({ path: '/unavailable', replace: true });
    return;
  }

  const outOfCatalog = moduleGate(to, useTenantStore());
  if (outOfCatalog) {
    next(outOfCatalog);
    return;
  }

  next();
});

router.afterEach((to) => {
  const canonicalUrl = (to.meta.canonical as string) || window.location.href;
  useHead({
    link: [
      {
        rel: 'canonical',
        href: canonicalUrl,
      },
    ],
  });
});

const knowErrors = [
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
];
router.onError((error, to) => {
  if (knowErrors.some((msg) => (error as Error).message.includes(msg))) {
    // Strip OAuth code from URL before hard-navigating — a stale chunk failure
    // while landing on /?code=xxx would otherwise replay the already-consumed code.
    const url = new URL(to.fullPath, window.location.origin);
    url.searchParams.delete('code');
    (window as Window).location = url.pathname + url.search + url.hash || '/';
  }
});
// https://vite.dev/guide/build#load-error-handling
// El handler "router.onError" rutea a la última ruta visitada/intento de visita en caso de error.
// window.addEventListener('vite:preloadError', (_event) => {
//   window.location.reload();
// });

export default router;
